import React, { createContext, useContext, useEffect, useState } from 'react'
import api from '../lib/api'
import { useAuth } from './AuthContext'

const BranchContext = createContext({})

export const useBranch = () => useContext(BranchContext)

export const BranchProvider = ({ children }) => {
  const { profile, isAuthenticated } = useAuth()
  const [branches, setBranches] = useState([])
  const [selectedBranch, setSelectedBranch] = useState(null) // null = all branches (vendor), or branch object

  useEffect(() => {
    if (!isAuthenticated || !profile) {
      setBranches([])
      setSelectedBranch(null)
      return
    }

    api.get('/branches', { params: { is_active: 'true' } })
      .then(({ data }) => {
        const list = data || []
        setBranches(list)

        if (['staff', 'trainer'].includes(profile.role)) {
          // Staff/trainer are locked to their assigned branch
          const assigned = list.find(b => b.id === profile.branch_id) || null
          setSelectedBranch(assigned)
        } else if (list.length === 1) {
          // Vendor with single branch — auto-select it
          setSelectedBranch(list[0])
        } else {
          // Vendor with multiple branches — restore saved preference if any
          const savedId = profile.preferences?.preferred_branch_id
          const saved = savedId ? list.find(b => b.id === savedId) || null : null
          setSelectedBranch(saved)
        }
      })
      .catch(() => {
        setBranches([])
        setSelectedBranch(null)
      })
  }, [profile?.id, isAuthenticated])

  // User-triggered branch change — updates state and persists preference to profile
  const changeSelectedBranch = (branch) => {
    setSelectedBranch(branch)
    api.patch('/profiles/me', {
      preferences: { ...(profile?.preferences || {}), preferred_branch_id: branch?.id || null },
    }).catch(() => {})
  }

  const value = {
    branches,
    selectedBranch,
    setSelectedBranch,
    changeSelectedBranch,
    selectedBranchId: selectedBranch?.id || null,
  }

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  )
}
